import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Textarea } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import {
    LIMITE_DO_DISPARO, MENSAGEM_MAXIMA, personalizar, problemaDaMensagemDaCampanha, resumoDoDisparo,
} from '@/data/disparoDeCampanha';
import { dados } from '@/data/porta';
import type { Campanha } from '@/data/tipos';

export interface DisparoDaCampanhaProps {
    campanha: Campanha | null;
    canalConectado: boolean;
    onClose: () => void;
    onDisparado: () => void;
}

/**
 * Disparar a campanha pelo WhatsApp — em dois passos, e o segundo é a conta.
 *
 * Antes o Marketing parava no público: "serve para trabalhar a lista à mão".
 * Agora a pessoa escreve, vê a prévia com o nome de alguém do público, e só
 * então confirma com o número exato de quem vai receber. Nada sai na hora: a
 * campanha entra na fila do agendador, e o aviso diz isso.
 */
export function DisparoDaCampanha({ campanha, canalConectado, onClose, onDisparado }: DisparoDaCampanhaProps) {
    const aviso = useAviso();
    const idMensagem = useId();
    const [publico, setPublico] = useState<{ contatoId: string; nome: string }[] | null>(null);
    const [erroDoPublico, setErroDoPublico] = useState<string | null>(null);
    const [mensagem, setMensagem] = useState('');
    const [confirmando, setConfirmando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (!campanha) return;
        let vivo = true;
        setPublico(null);
        setErroDoPublico(null);
        setMensagem('');
        setConfirmando(false);
        setErro(null);
        dados().publicoDaCampanha(campanha.id)
            .then((p) => { if (vivo) setPublico(p); })
            .catch((e: unknown) => { if (vivo) setErroDoPublico(e instanceof Error ? e.message : 'Não foi possível calcular o público.'); });
        return () => { vivo = false; };
    }, [campanha]);

    if (!campanha) return null;

    const problema = problemaDaMensagemDaCampanha(mensagem);
    const quantos = publico?.length ?? 0;
    const excede = quantos > LIMITE_DO_DISPARO;
    const podeRevisar = canalConectado && publico !== null && quantos > 0 && !excede && !problema;
    const exemplo = publico?.[0]?.nome;

    const disparar = async () => {
        setEnviando(true);
        setErro(null);
        try {
            const r = await dados().dispararCampanha(campanha.id, mensagem);
            aviso({ title: 'Campanha na fila de envio', description: resumoDoDisparo(r) });
            onDisparado();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível disparar a campanha.');
            setConfirmando(false);
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={560}
            title={`Disparar ${campanha.nome}`}
            description="A mensagem vai pelo WhatsApp da clínica, para o público calculado agora."
            dirty={mensagem.trim().length > 0 && !enviando}
            footer={confirmando ? (
                <>
                    <Button variant="ghost" onClick={() => setConfirmando(false)} disabled={enviando}>Voltar</Button>
                    <Button onClick={() => { void disparar(); }} loading={enviando}>
                        {`Enviar para ${quantos} ${quantos === 1 ? 'pessoa' : 'pessoas'}`}
                    </Button>
                </>
            ) : (
                <>
                    <Button variant="ghost" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => setConfirmando(true)} disabled={!podeRevisar}>Revisar o disparo</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                {!canalConectado && (
                    <Alert tone="warning" dismissible={false} title="Nenhum WhatsApp conectado">
                        A campanha não teria por onde sair. Conecte o WhatsApp da clínica em{' '}
                        <Link to="/app/configuracoes/integracoes" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                            Configurações › Integrações
                        </Link>
                        .
                    </Alert>
                )}

                {erroDoPublico && <Alert tone="danger" dismissible={false} title="O público não foi calculado">{erroDoPublico}</Alert>}
                {publico === null && !erroDoPublico && (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Calculando o público…</p>
                )}
                {publico !== null && (
                    <p style={{ margin: 0, fontSize: 13.5 }}>
                        <strong>{quantos} {quantos === 1 ? 'pessoa' : 'pessoas'}</strong> no público agora.
                    </p>
                )}
                {excede && (
                    <Alert tone="warning" dismissible={false} title={`O disparo vai a no máximo ${LIMITE_DO_DISPARO} pessoas por vez`}>
                        Recorte o público — por origem, por exemplo — antes de disparar.
                    </Alert>
                )}

                {confirmando ? (
                    <>
                        <Alert tone="info" dismissible={false} title="Confirme antes de enviar">
                            {quantos} {quantos === 1 ? 'pessoa vai' : 'pessoas vão'} receber esta mensagem pelo WhatsApp. Ela sai pela
                            fila, 40 pessoas a cada 5 minutos. Quem pediu outro canal ou está sem telefone completo fica de
                            fora, e ninguém recebe esta campanha duas vezes.
                        </Alert>
                        <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', fontSize: 13.5, whiteSpace: 'pre-wrap' }}>
                            {personalizar(mensagem.trim(), exemplo)}
                        </div>
                    </>
                ) : (
                    <>
                        <FormField
                            label="Mensagem" htmlFor={idMensagem} required
                            hint={`{nome} vira o primeiro nome de cada pessoa. De 10 a ${MENSAGEM_MAXIMA} caracteres.`}
                        >
                            <Textarea
                                id={idMensagem} value={mensagem} onChange={setMensagem} rows={5}
                                placeholder="Olá, {nome}! Faz tempo que não vemos você por aqui…"
                            />
                        </FormField>
                        {mensagem.trim() && exemplo && (
                            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                Para {exemplo}: “{personalizar(mensagem.trim(), exemplo)}”
                            </p>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
